import { useTranslation } from "react-i18next";

import { Typography, Radio } from "@aviary";
import type {
  ProductSortField,
  ProductSortDirection,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { CatalogSortDropdown } from "@shared/components/CatalogSortDropdown/CatalogSortDropdown";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";

import * as styles from "./Sort.styles";

type SortOption = [ProductSortField, ProductSortDirection];

interface Props {
  isBold?: boolean;
  isDropdown?: boolean;
}

const Sort = ({ isBold, isDropdown }: Props) => {
  const { t } = useTranslation();
  const { isClinic } = useSharedGlobalConfig();

  const radioName = "catalog_result_sort";

  const alphabeticalAsc: SortOption = ["ALPHABETICAL", "ASC"];
  const alphabeticalDesc: SortOption = ["ALPHABETICAL", "DESC"];
  const bestMatchDesc: SortOption = ["RELEVANCE", "DESC"];
  const mostPrescribedDesc: SortOption = ["MOST_PRESCRIBED", "DESC"];
  const priceAsc: SortOption = ["PRICE", "ASC"];
  const priceDesc: SortOption = ["PRICE", "DESC"];

  const { sortDirection, sortField, setSort } = useCatalogSearch();

  const isRadioChecked = ([radioField, radioSortDirection]: SortOption) =>
    radioField === sortField && radioSortDirection === sortDirection;

  const onRadioClick = ([radioField, radioSortDirection]: SortOption) =>
    setSort(radioField, radioSortDirection);

  const renderMostPrescribedRadio = () => {
    if (isClinic) {
      return (
        <Radio
          labelStyle={styles.radioTopMargin}
          name={radioName}
          checked={isRadioChecked(mostPrescribedDesc)}
          onChange={() => onRadioClick(mostPrescribedDesc)}
        >
          <Typography>{t(l.sharedCatalog.resultSort.MostRecommended)}</Typography>
        </Radio>
      );
    }

    return null;
  };

  if (isDropdown) {
    return (
      <div css={styles.sort}>
        <CatalogSortDropdown isDisabled={false} isFullWidth isTransparent={false} />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <Typography isSecondaryWeight={isBold}>{t(l.sharedCatalog.filter.Sort)}</Typography>
      <Radio
        name={radioName}
        checked={isRadioChecked(bestMatchDesc)}
        onChange={() => onRadioClick(bestMatchDesc)}
      >
        <Typography>{t(l.sharedCatalog.resultSort.BestMatch)}</Typography>
      </Radio>
      {renderMostPrescribedRadio()}
      <Radio
        labelStyle={styles.radioTopMargin}
        name={radioName}
        checked={isRadioChecked(alphabeticalAsc)}
        onChange={() => onRadioClick(alphabeticalAsc)}
      >
        <Typography>{t(l.sharedCatalog.resultSort.AlphabeticalAZ)}</Typography>
      </Radio>
      <Radio
        labelStyle={styles.radioTopMargin}
        name={radioName}
        checked={isRadioChecked(alphabeticalDesc)}
        onChange={() => onRadioClick(alphabeticalDesc)}
      >
        <Typography>{t(l.sharedCatalog.resultSort.AlphabeticalZA)}</Typography>
      </Radio>
      <Radio
        labelStyle={styles.radioTopMargin}
        name={radioName}
        checked={isRadioChecked(priceDesc)}
        onChange={() => onRadioClick(priceDesc)}
      >
        <Typography>{t(l.sharedCatalog.resultSort.MostExpensive)}</Typography>
      </Radio>
      <Radio
        labelStyle={styles.radioTopMargin}
        name={radioName}
        checked={isRadioChecked(priceAsc)}
        onChange={() => onRadioClick(priceAsc)}
      >
        <Typography>{t(l.sharedCatalog.resultSort.LeastExpensive)}</Typography>
      </Radio>
    </div>
  );
};

export { Sort };
